import React, { useState, useRef } from 'react'
import SignatureCanvas from 'react-signature-canvas'
import { Report } from 'notiflix/build/notiflix-report-aio'
import axios from 'axios'
import { browserName, osName } from 'react-device-detect'
import { InputCheckbox } from '../../reusable/inputCheckbox'
import { SignatureActionBar } from '../../reusable/signatureActionBar'
import { DocumentPreviewCard } from '../../reusable/documentPreviewCard'
import { FormPartInterface } from '../../../interfaces'
import { useClaimData } from '../../../claimProvider'
import { trackSubmitApplication } from '../../../helpers/handleTracking'

import { Lead, FinePrint } from '../../ui/Text'
import HeroHeading from '../../sections/HeroHeading'
import styles from './styles.module.scss'
import sigPadStyles from '../../reusable/sigPad/styles.module.scss'

type PackDocument = {
    id: string
    title: string
    url?: string | null
    description?: string
}

const FALLBACK_DOC_DESCRIPTIONS: Record<string, string> = {
    'bridging-agreement': 'Agreement bridging your matter with Sentinel Legal. Please review carefully before signing.',
    'conditional-fee-agreement': 'Your Conditional Fee Agreement (CFA) with Sentinel Legal.',
    'pre-action-contingency-fee': 'Pre-action contingency fee agreement for your claim.',
    'foa-service-records': 'Authority to obtain your military service records.',
    'mod-sar-request': 'MOD subject access request for your records.',
    'civilian-medical-consent': 'Consent for civilian medical records (where applicable).',
    'hmrc-employment-history': 'HMRC employment history request form.',
}

/** Exact copy shown on the checkbox — also sent in the sign payload for audit. */
const CONSENT_DISPLAY_NAME = 'I confirm that I have read and understood the documents below'
const CONSENT_SUPPORTING_TEXT =
    'and authorise Sentinel Legal to apply my signature to them and pursue my hearing loss claim on my behalf.'

export const SignatureHearingLoss = (props: FormPartInterface) => {
    const { saveValueFunct, isActive, assignGroup } = props
    const [sigData, setSigData] = useState('')
    const [acceptTermsChecked, setAcceptTermsChecked] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const signCanvas = useRef<SignatureCanvas>(null) as React.MutableRefObject<any>
    const signatureSectionRef = useRef<HTMLDivElement>(null)
    const consentSectionRef = useRef<HTMLDivElement>(null)
    const claim = useClaimData()

    const packDocuments: PackDocument[] = Array.isArray(claim?.documents)
        ? claim.documents
        : claim?.documentUrl
          ? [
                {
                    id: 'hearing-loss-claim',
                    title: 'Hearing Loss Claim Document',
                    url: claim.documentUrl,
                    description: 'Your military hearing loss claim pack. Please review it carefully before signing.',
                },
            ]
          : []

    const hasSignature = sigData.length > 0
    const allConsentsConfirmed = acceptTermsChecked
    const formReady = hasSignature && allConsentsConfirmed
    const consentProgressLabel = `${acceptTermsChecked ? 1 : 0} of 1 confirmed`
    const blockedMessage = !hasSignature
        ? 'Please add your signature above to continue.'
        : !acceptTermsChecked
          ? 'Please confirm you have read and understood the documents.'
          : null

    const onAcceptTermsChange = (checked: boolean) => {
        setAcceptTermsChecked(checked)
        saveValueFunct({ field: 'acceptTerms', value: checked ? 'yes' : 'no' })
    }

    const saveSign = () => {
        signCanvas.current.on()
        setSigData(signCanvas.current.toDataURL('image/png'))
    }

    const clearSign = (e: React.MouseEvent) => {
        e.preventDefault()
        signCanvas.current.clear()
        signCanvas.current.on()
        setSigData('')
    }

    const scrollToIncomplete = () => {
        const target = !hasSignature ? signatureSectionRef.current : consentSectionRef.current
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    const submitSignature = () => {
        if (!formReady) {
            scrollToIncomplete()
            return
        }

        // The Submit button shows the loading state (no full-screen loader), and
        // stays loading until the upload page takes over
        setIsSubmitting(true)

        axios
            .post(`${process.env.REACT_APP_API_HEARING_LOSS_SIGN}`, {
                type: 'mhl-esign-signature',
                token: claim?.token,
                sigData,
                browser: browserName,
                device: osName,
                consent: {
                    checked: acceptTermsChecked === true,
                    displayName: CONSENT_DISPLAY_NAME,
                    supportingText: CONSENT_SUPPORTING_TEXT,
                },
            })
            .then((response) => {
                // Signed already (e.g. in another tab, or a double submit): say so, then
                // reload so the sign session sends them on to uploads or the finish
                if (response?.data?.success === false && /already signed/i.test(String(response.data.error || ''))) {
                    setIsSubmitting(false)
                    Report.info(
                        'Already signed',
                        "You've already signed these documents, so there's nothing more to sign. We'll take you to the next step.",
                        'Continue',
                        () => window.location.reload()
                    )
                    return
                }
                if (!response?.data?.success) {
                    setIsSubmitting(false)
                    Report.failure(
                        'An Error Occurred',
                        'We have encountered an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                        'Okay'
                    )
                    return
                }
                trackSubmitApplication()
                setTimeout(() => assignGroup('upload'), 400)
            })
            .catch((error) => {
                console.log(error)
                setIsSubmitting(false)
                Report.failure(
                    'An Error Occurred',
                    'We have encountered an internal error, please try again later. If the problem persists please contact support@sentinellegal.co.uk',
                    'Okay'
                )
            })
    }

    if (isActive !== 'signature' || packDocuments.length < 1) {
        return <></>
    }

    // Copy matches the form-2026 sign page
    const firstName = claim?.clientName?.trim().split(/\s+/)[0] ?? ''

    return (
        <div className={`${styles.container} ${styles.withActionBar}`}>
            {/* Same title treatment as the landing page hero */}
            <HeroHeading
                brandBar={false}
                title={
                    <>
                        Review and sign
                        <br />
                        your documents
                        {firstName && <span className="mf-excluded">, {firstName}</span>}
                    </>
                }
            />
            <div className={styles.constrained}>
                <Lead align="center">
                    Your application details have been submitted. Please review your documents and provide your
                    signature to complete your instruction.
                </Lead>
            </div>

            <div
                ref={signatureSectionRef}
                className={`${sigPadStyles.signaturePadContent} ${styles.constrained}`}
                data-form-block-type="signature"
            >
                <div className={sigPadStyles.sigPadContainer}>
                    <p>Please use your mouse, finger or stylus to draw your signature below.</p>
                    <div>
                        <SignatureCanvas
                            ref={signCanvas}
                            onEnd={saveSign}
                            clearOnResize={false}
                            penColor="grey"
                            canvasProps={{
                                width: window.innerWidth < 760 ? 350 : 760,
                                height: 300,
                                className: 'sigCanvas mf-masked mf-listen',
                            }}
                        />
                        <span onClick={clearSign} style={{ cursor: 'pointer' }}>
                            Clear
                        </span>
                    </div>
                </div>

                <div
                    ref={consentSectionRef}
                    className={styles.checkboxWrapper}
                    style={{ marginBottom: '1rem', marginTop: '1rem' }}
                    data-form-block-type="consent_group"
                >
                    <InputCheckbox
                        id="mhl-accept-terms"
                        displayName={CONSENT_DISPLAY_NAME}
                        supportingText={CONSENT_SUPPORTING_TEXT}
                        value={acceptTermsChecked}
                        onChange={onAcceptTermsChange}
                    />
                </div>

                <FinePrint className={styles.signingNote}>
                    By signing these documents, you confirm the information above is accurate and you authorise Sentinel
                    Legal to act on your behalf in pursuing your military hearing loss claim.
                </FinePrint>
                <FinePrint className={styles.signingNote}>
                    A signed copy will be emailed to you and stored securely against your case reference.
                </FinePrint>
            </div>

            <div className={styles.documentsSection}>
                {packDocuments.map((doc) => (
                    <div className={styles.documentItem} key={doc.id}>
                        <DocumentPreviewCard
                            title={doc.title}
                            description={
                                doc.description ||
                                FALLBACK_DOC_DESCRIPTIONS[doc.id] ||
                                'Please review this document carefully before signing.'
                            }
                            url={doc.url}
                            actionLabel={doc.title}
                        />
                    </div>
                ))}
            </div>

            <SignatureActionBar
                onSubmit={submitSignature}
                isSubmitBlocked={!formReady}
                blockedMessage={blockedMessage}
                isSubmitLoading={isSubmitting}
                consentProgressLabel={consentProgressLabel}
                hasSignature={hasSignature}
                allConsentsConfirmed={allConsentsConfirmed}
            />
        </div>
    )
}
